/**
 * Lifecycle of a phone number Atlas provisioned from Twilio.
 *
 * Ops hands these numbers to clients, who register them on their customer
 * portals (Costco, Walmart, Target) to receive the portal's SMS/voice
 * verification codes. A portal account holds a number exclusively, so a client
 * onboarding a new portal needs a fresh number — which is why numbers are
 * tracked as owned inventory rather than a free-text field on the client.
 */
export enum TwilioPhoneNumberStatus {
  /** Bought and receiving, but not yet attached to any client. */
  Unassigned = 'unassigned',
  /** Attached to at least one client. */
  Active = 'active',
  /**
   * Held back: a portal rejected it for being a VoIP line and demands a real
   * mobile number. Nothing Twilio offers fixes that, so the number stays on the
   * account but is flagged so ops does not burn time re-registering it against
   * the same restriction. Number-wide: a flagged number cannot be assigned to
   * any client until the flag is cleared.
   */
  Unsupported = 'unsupported',
  /**
   * No longer ours, or deliberately retired. Terminal — a released number keeps
   * its row so the message history it received stays attributable.
   */
  Released = 'released',
}

/** Lifecycle of one client's assignment on a number. */
export enum TwilioAssignmentStatus {
  /**
   * Live. A client is attached to a given number at most once while live; a
   * client may hold several numbers, and a number may serve several clients.
   */
  Active = 'active',
  /** Ended — the client relationship or the portal registration is over. Terminal. */
  Released = 'released',
}

/**
 * Which Twilio callback delivered an inbound message.
 *
 * SMS and voice are the two channels a phone number can receive a verification
 * code on, so they are the two Twilio can serve.
 *
 * On the authenticator-app question, which the task left open: some customer
 * portals offer (or insist on) a TOTP authenticator app instead of texting a
 * code. That is NOT a third channel here and cannot become one — a TOTP code is
 * derived locally from a shared secret plus the clock and is never transmitted,
 * so no phone number ever receives it and Twilio has no part to play. Twilio's
 * own Verify TOTP product is the inverse of what ops needs: it issues factors
 * for OUR users to enrol, not a way to hold a factor a THIRD party (the portal)
 * issued to us.
 *
 * Supporting those portals would be a separate feature with a different shape:
 * store the base32 secret from the portal's enrolment QR code, encrypted per
 * client the way other credentials are, and generate the 6-digit code on
 * demand (`otplib`/`speakeasy`, or Node's own HMAC — RFC 6238 is a dozen
 * lines). It shares only the client relationship with this integration and
 * none of the number inventory, which is why it is out of scope here rather
 * than deferred inside it.
 */
export enum TwilioMessageChannel {
  Sms = 'sms',
  Voice = 'voice',
}

/**
 * Only inbound is recorded. Atlas never sends from these numbers — they exist to
 * receive codes — but the column is explicit so an outbound log stays possible
 * without a migration.
 */
export enum TwilioMessageDirection {
  Inbound = 'inbound',
}

/**
 * What a number can receive, as reported by Twilio at purchase time. Recorded
 * because a portal that verifies by voice call cannot use an SMS-only number.
 */
export interface ITwilioPhoneNumberCapabilities {
  sms: boolean
  voice: boolean
  mms: boolean
}

/** A number as the ops phone-number list renders it. */
export interface ITwilioPhoneNumberRow {
  id: string
  phoneNumber: string
  twilioSid: string
  status: TwilioPhoneNumberStatus
  friendlyName: string | null
  capabilities: ITwilioPhoneNumberCapabilities | null
  notes: string | null
  createdAt: Date
  releasedAt: Date | null
  /** Live client assignments. More than one means a shared portal login. */
  assignments: ITwilioAssignmentRow[]
}

/** One client's assignment on a number. */
export interface ITwilioAssignmentRow {
  id: string
  phoneNumberId: string
  clientId: string | null
  clientName: string
  status: TwilioAssignmentStatus
  /**
   * Free text. In practice which portal the client registered the number on
   * ("Walmart portal login") — portals are deliberately not modelled yet.
   */
  notes: string | null
  assignedAt: Date
  releasedAt: Date | null
}

/**
 * A received message, with the client(s) it belongs to resolved from the
 * assignments that were live when it arrived — so reassigning a number does not
 * retag its history, and a number shared across clients reports all of them.
 */
export interface ITwilioMessageRow {
  id: string
  phoneNumberId: string | null
  phoneNumber: string
  fromNumber: string
  channel: TwilioMessageChannel
  /**
   * The code as text: an SMS body verbatim, or Twilio's transcription of a
   * spoken one. Null for a call until the recording callback fills it in, and
   * for good if Twilio could not make the digits out — hence `recordingUrl`.
   */
  body: string | null
  /** The call audio, once Twilio has delivered it. Always null for SMS. */
  recordingUrl: string | null
  receivedAt: Date
  tags: ITwilioMessageTag[]
}

export interface ITwilioMessageTag {
  clientName: string
}

/** A number search result, trimmed to what the provisioning UI shows. */
export interface ITwilioAvailableNumber {
  phoneNumber: string
  friendlyName: string
  locality: string | null
  region: string | null
  isoCountry: string
  capabilities: ITwilioPhoneNumberCapabilities
}

/** A number we now own, as Twilio described it at purchase time. */
export interface ITwilioProvisionedNumber {
  sid: string
  phoneNumber: string
  friendlyName: string | null
  capabilities: ITwilioPhoneNumberCapabilities
}

/** What a local number costs to keep, as Twilio prices them today. */
export interface ITwilioNumberPrice {
  /** Monthly rental, as a decimal string exactly as Twilio reports it. */
  monthly: string
  /** ISO currency of {@link monthly}, e.g. 'USD'. */
  currency: string
}
